import { Link } from 'react-router-dom';
import { MapPin, Star, Building2, Home, Car } from 'lucide-react';
import { ProviderBadge } from '../ProviderBadge';
import { formatAddress } from '../../utils/format';

export function ServiceProviderCard({ provider }) {
  if (!provider) return null;

  return (
    <div className="space-y-4 mb-6">
      <Link 
        to={provider.username ? `/@${provider.username}` : `/provider/${provider.provider_id}`}
        className="card-surface p-6 flex items-center gap-4 card-hover"
        data-testid="provider-link"
      >
        <div className="w-16 h-16 rounded-full bg-[#D4AF37] flex-shrink-0 flex items-center justify-center text-white text-2xl font-heading overflow-hidden">
          {provider.profile_image ? (
            <img src={provider.profile_image} alt={provider.brand_name} className="w-full h-full object-cover" />
          ) : (
            provider.brand_name?.charAt(0)
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-xl font-medium text-[#1A1A1A] flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="break-words">{provider.brand_name}</span>
            {provider.badge && <ProviderBadge badge={provider.badge} size="xs" />}
            {provider.top_rated && <ProviderBadge badge="top_rated" size="xs" />}
          </h3>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-stone-500 mt-1">
            <span className="flex items-center gap-1 whitespace-nowrap">
              <MapPin className="h-4 w-4 flex-shrink-0" />
              {provider.location}
            </span>
            {provider.rating > 0 && (
              <span className="flex items-center gap-1 text-[#D4AF37] whitespace-nowrap">
                <Star className="h-4 w-4 fill-current flex-shrink-0" />
                {provider.rating.toFixed(1)} ({provider.review_count} {provider.review_count === 1 ? 'review' : 'reviews'})
              </span>
            )}
          </div>
        </div>
      </Link>

      {/* Location Details */}
      {provider.service_types && provider.service_types.length > 0 && (
        <div className="card-surface p-5 space-y-3" data-testid="provider-location-details">
          <h4 className="font-medium text-[#1A1A1A] text-sm flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[#D4AF37]" />
            Service Locations
          </h4>
          <div className="space-y-2">
            {provider.service_types.includes('salon') && (
              <div className="flex items-start gap-3 text-sm" data-testid="salon-location-info">
                <Building2 className="h-4 w-4 text-stone-400 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-[#1A1A1A]">Salon / Studio</span>
                  {provider.salon_address && (
                    <p className="text-stone-500">{formatAddress(provider.salon_address)}</p>
                  )}
                </div>
              </div>
            )}
            {provider.service_types.includes('home') && (
              <div className="flex items-start gap-3 text-sm" data-testid="home-location-info">
                <Home className="h-4 w-4 text-stone-400 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-[#1A1A1A]">Home-based</span>
                  {(provider.home_address || provider.home_address_partial) && (
                    <p className="text-stone-500">
                      {formatAddress(provider.home_address) || formatAddress(provider.home_address_partial)}
                      {!provider.home_address && provider.home_address_partial && (
                        <span className="text-xs text-stone-400 block mt-0.5">Full address provided after booking confirmation</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
            )}
            {provider.service_types.includes('mobile') && (
              <div className="flex items-start gap-3 text-sm" data-testid="mobile-location-info">
                <Car className="h-4 w-4 text-stone-400 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-[#1A1A1A]">Mobile</span>
                  {provider.service_radius && (
                    <p className="text-stone-500">Travels up to {provider.service_radius} {provider.service_radius === 1 ? 'mile' : 'miles'}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
